import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import BrandNavbar from '../components/dashboard/BrandNavbar';
import api from '../api';

const BrandCreatorsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [creators, setcreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  // Filter states
  const [filters, setFilters] = useState({
    state: '',
    city: '',
    interest: '',
    platform: '',
    minFollowers: '',
    maxFollowers: ''
  });

  // Filter options
  const [filterOptions, setFilterOptions] = useState({
    states: [],
    cities: [],
    interests: [],
    platforms: []
  });

  const [sortBy, setSortBy] = useState('followers');
  const [sortDirection, setSortDirection] = useState('DESC');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    if (!user || user.role !== 'brand') return;
    fetchFilterOptions();
    fetchcreators();
  }, [user, currentPage, sortBy, sortDirection]);

  const fetchFilterOptions = async () => {
    try {
      const response = await api.get(`/brands/${user.userId}/creators/filter-options`);
      setFilterOptions(response.data);
    } catch (error) {
      // Error handled silently
    }
  };

  const fetchcreators = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        size: '12',
        sortBy,
        sortDirection
      });

      // Add filters if set
      if (filters.state) params.append('state', filters.state);
      if (filters.city) params.append('city', filters.city);
      if (filters.interest) params.append('interest', filters.interest);
      if (filters.platform) params.append('platform', filters.platform);
      if (filters.minFollowers) params.append('minFollowers', filters.minFollowers);
      if (filters.maxFollowers) params.append('maxFollowers', filters.maxFollowers);

      const response = await api.get(`/brands/${user.userId}/creators?${params}`);
      setcreators(response.data.creators || []);
      setTotalPages(response.data.totalPages || 0);
      setTotalElements(response.data.totalElements || 0);
    } catch (error) {
      setcreators([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
  };

  const applyFilters = () => {
    setCurrentPage(0);
    fetchcreators();
  };

  const clearFilters = () => {
    setFilters({
      state: '',
      city: '',
      interest: '',
      platform: '',
      minFollowers: '',
      maxFollowers: ''
    });
    setCurrentPage(0);
    setTimeout(() => fetchcreators(), 100);
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(field);
      setSortDirection('DESC');
    }
  };

  if (!user) return <p className="p-8 text-center">Please log in to view creators.</p>;
  if (user.role !== 'brand') return <p className="p-8 text-center">Access denied.</p>;

  return (
    <>
      <BrandNavbar />
      <div className="pt-24 min-h-screen dashboard-shell">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="app-card rounded-[2rem] p-6 sm:p-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between mb-6">
            <div>
              <div className="section-kicker mb-4">Creator discovery</div>
              <h1 className="text-3xl font-bold text-gray-900">Discover creators</h1>
              <p className="text-gray-600 mt-2 max-w-2xl">
                Explore better-fit creator profiles with clearer filters, stronger cards, and faster scanning.
              </p>
              <p className="text-gray-600 mt-2">
                {totalElements} creator{totalElements !== 1 ? 's' : ''} found
              </p>
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="primary-button !px-5 !py-2.5 !text-sm flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </button>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="app-card rounded-[1.75rem] p-6 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* State Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                  <select
                    value={filters.state}
                    onChange={(e) => handleFilterChange('state', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">All States</option>
                    {filterOptions.states.map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>

                {/* City Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                  <select
                    value={filters.city}
                    onChange={(e) => handleFilterChange('city', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">All Cities</option>
                    {filterOptions.cities.map(city => (
                      <option key={city} value={city}>{city}</option>
                    ))}
                  </select>
                </div>

                {/* Interest Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Interest/Category</label>
                  <select
                    value={filters.interest}
                    onChange={(e) => handleFilterChange('interest', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">All Interests</option>
                    {filterOptions.interests.map(interest => (
                      <option key={interest} value={interest}>{interest}</option>
                    ))}
                  </select>
                </div>

                {/* Platform Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Platform</label>
                  <select
                    value={filters.platform}
                    onChange={(e) => handleFilterChange('platform', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">All Platforms</option>
                    {filterOptions.platforms.map(platform => (
                      <option key={platform} value={platform}>{platform}</option>
                    ))}
                  </select>
                </div>

                {/* Min Followers */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Min Followers</label>
                  <input
                    type="number"
                    value={filters.minFollowers}
                    onChange={(e) => handleFilterChange('minFollowers', e.target.value)}
                    placeholder="e.g., 1000"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Max Followers */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Max Followers</label>
                  <input
                    type="number"
                    value={filters.maxFollowers}
                    onChange={(e) => handleFilterChange('maxFollowers', e.target.value)}
                    placeholder="e.g., 100000"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Filter Actions */}
              <div className="flex gap-3 mt-4">
                <button
                  onClick={applyFilters}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
                >
                  Apply Filters
                </button>
                <button
                  onClick={clearFilters}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-6 py-2 rounded-lg transition-colors"
                >
                  Clear All
                </button>
              </div>
            </div>
          )}

          {/* Sort Options */}
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <span className="text-sm text-gray-600">Sort by:</span>
            <button
              onClick={() => handleSort('followers')}
              className={`px-4 py-2 rounded-full text-sm ${sortBy === 'followers' ? 'bg-collaborb-inner text-white' : 'bg-white border border-slate-200 text-gray-700'}`}
            >
              Followers {sortBy === 'followers' && (sortDirection === 'ASC' ? '↑' : '↓')}
            </button>
            <button
              onClick={() => handleSort('name')}
              className={`px-4 py-2 rounded-full text-sm ${sortBy === 'name' ? 'bg-collaborb-inner text-white' : 'bg-white border border-slate-200 text-gray-700'}`}
            >
              Name {sortBy === 'name' && (sortDirection === 'ASC' ? '↑' : '↓')}
            </button>
            <button
              onClick={() => handleSort('location')}
              className={`px-4 py-2 rounded-full text-sm ${sortBy === 'location' ? 'bg-collaborb-inner text-white' : 'bg-white border border-slate-200 text-gray-700'}`}
            >
              Location {sortBy === 'location' && (sortDirection === 'ASC' ? '↑' : '↓')}
            </button>
          </div>

          {/* Loading State */}
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading creators...</p>
            </div>
          ) : creators.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg shadow">
              <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No creators found</h3>
              <p className="text-gray-600">Try adjusting your filters to see more results</p>
            </div>
          ) : (
            <>
              {/* creators Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {creators.map((creator) => (
                  <div
                    key={creator.id}
                    className="app-card rounded-[1.75rem] hover:-translate-y-1 transition-transform duration-200 overflow-hidden cursor-pointer"
                    onClick={() => navigate(`/creator/${creator.userId}`)}
                  >
                    <div className="p-6">
                      {/* Profile Picture */}
                      <div className="flex items-start gap-4 mb-4">
                        {creator.profilePictureUrl ? (
                          <img
                            src={creator.profilePictureUrl}
                            alt={creator.name}
                            className="w-16 h-16 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-16 h-16 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-xl">
                            {creator.name?.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div className="flex-1">
                          <h3 className="font-bold text-lg text-gray-900">{creator.name}</h3>
                          {creator.location && (
                            <p className="text-sm text-gray-500 flex items-center gap-1">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              </svg>
                              {creator.location}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Bio */}
                      {creator.bio && (
                        <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                          {creator.bio}
                        </p>
                      )}

                      {/* Followers */}
                      <div className="flex items-center gap-2 mb-4">
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <span className="font-semibold text-blue-600">
                          {creator.followers?.toLocaleString('en-IN')}
                        </span>
                        <span className="text-sm text-gray-500">followers</span>
                      </div>

                      {/* Interests */}
                      {creator.interests && creator.interests.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-4">
                          {creator.interests.slice(0, 3).map((interest, idx) => (
                            <span
                              key={idx}
                              className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full"
                            >
                              {interest.trim()}
                            </span>
                          ))}
                          {creator.interests.length > 3 && (
                            <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                              +{creator.interests.length - 3} more
                            </span>
                          )}
                        </div>
                      )}

                      {/* Platforms */}
                      {creator.platforms && creator.platforms.length > 0 && (
                        <div className="flex gap-2">
                          {creator.platforms.map((platform, idx) => (
                            <span
                              key={idx}
                              className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded"
                            >
                              {platform.trim()}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* View Profile Button */}
                      <button
                        className="w-full mt-4 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/creator/${creator.userId}`);
                        }}
                      >
                        View Profile
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-8">
                  <button
                    onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                    disabled={currentPage === 0}
                    className="px-4 py-2 bg-white border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <span className="text-gray-600">
                    Page {currentPage + 1} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
                    disabled={currentPage >= totalPages - 1}
                    className="px-4 py-2 bg-white border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default BrandCreatorsPage;
